import { apiRoutes } from "./apiRoutes";
import { formatDate, timeString } from "./formatDate";

const REACT_API_ROUTE = process.env.REACT_APP_API_ROUTE;

let sessionExpiredHandled = false;

// Substitutes {{param}} placeholders in a translated error string. The
// backend sends raw values (e.g. an ISO date) via data.params rather than
// baking a server-locale-formatted string into the message, so it stays
// translatable — a param literally named "date" gets formatted here in the
// viewer's locale instead of being dropped in as a raw ISO string.
const interpolateErrorParams = (message, params) => {
  if (!params) return message;
  return Object.entries(params).reduce((msg, [key, value]) => {
    const displayValue =
      key === "date" ? `${formatDate(value)} ${timeString(value)}`.trim() : value;
    return msg.split(`{{${key}}}`).join(displayValue);
  }, message);
};

// Backend errors carry an errorKey (e.g. ERROR_PERSON_NOT_FOUND) that maps to
// a translated string via t(). Falls back to the backend's raw English
// `errors` text when there's no t(), no errorKey, or no translation entry
// for it yet, so nothing breaks while translations are filled in.
export const resolveErrorMessage = (data, t, fallbackKey = "REQUEST_FAILED") => {
  const rawFallback = data?.errors
    ?.map((err) => (typeof err === "object" ? JSON.stringify(err) : err))
    .join(", ");

  if (!t) return rawFallback || fallbackKey;

  if (data?.errorKey) {
    const translated = t(data.errorKey);
    if (translated !== data.errorKey) {
      return interpolateErrorParams(translated, data.params);
    }
  }

  return rawFallback || t(fallbackKey);
};

const handleSessionExpired = (showAlert, navigate, t) => {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  // Coalesces a burst of concurrent 401s (e.g. data + avatars + notifications
  // all failing together) into a single alert/redirect, without permanently
  // latching — otherwise the very next session expiry after a fresh login
  // (same tab, no full reload) would silently no-op.
  setTimeout(() => {
    sessionExpiredHandled = false;
  }, 3000);

  if (typeof window !== "undefined") {
    localStorage.removeItem("authToken");
    localStorage.removeItem("responseLogin");
    window.dispatchEvent(new Event("auth-token-changed"));
  }

  if (showAlert && t) {
    showAlert(t("SESSION_EXPIRED"), "error");
  }

  if (navigate) {
    navigate("/login");
  } else if (typeof window !== "undefined") {
    window.location.href = "/login";
  }
};

export const getHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export const fetchData = async (
  API_URL,
  showAlert,
  navigate,
  t,
  includeAuth = true,
) => {
  console.log("FETCH DATA CALL STACK:", new Error().stack);
  const FULL_API_URL = `${REACT_API_ROUTE}${API_URL}`;
  console.log("Fetch", FULL_API_URL);

  try {
    const headers = includeAuth
      ? getHeaders()
      : { "Content-Type": "application/json" };

    const response = await fetch(FULL_API_URL, {
      method: "GET",
      headers,
    });

    console.log("fetch data before .json:", response);
    const data = await response.json();
    console.log("fetch data after .json:", data);

    if (!response.ok) {
      console.log("fetch data failed:", response.statusText);

      if (data?.status === "UNAUTHORIZED" || response.status === 401) {
        handleSessionExpired(showAlert, navigate, t);
        return null;
      }

      if (response.status === 403) {
        console.log("Access forbidden (403) for:", FULL_API_URL);
        return null;
      }

      const errorMessage = resolveErrorMessage(data, t, "FETCH_FAILED");
      showAlert?.(errorMessage, "error");
      return null;
    }

    return { data, headers: response.headers };
  } catch (error) {
    console.error("Error fetching data:", error);
    showAlert?.(t("NETWORK_ERROR"), "error");
    return null;
  }
};

export const fetchWithoutToc = async (API_URL) => {
  const FULL_API_URL = `${REACT_API_ROUTE}${API_URL}`;
  console.log("Fetch", FULL_API_URL);
  try {
    const response = await fetch(FULL_API_URL, {
      method: "GET",
    });

    console.log("response", response);

    if (response.ok) {
      return response;
    } else {
      console.error("fetch data failed:", response.statusText);
      throw new Error("Error fetch data");
    }
  } catch (error) {
    console.error("Error:", error);
    return [];
  }
};

export const createData = async (
  API_URL,
  dataObject,
  showAlert,
  navigate,
  t,
  onErrorData,
) => {
  const FULL_API_URL = `${REACT_API_ROUTE}${API_URL}`;
  console.log("Create", FULL_API_URL);
  console.log(dataObject);

  try {
    const response = await fetch(FULL_API_URL, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(dataObject),
    });

    const data = await response.json();

    if (!response.ok) {
      if (data?.status === "UNAUTHORIZED" || response.status === 401) {
        handleSessionExpired(showAlert, navigate, t);
        return null;
      }

      // Lets a caller intercept a specific backend errorKey (e.g. to open a
      // custom dialog using data.params) instead of the generic toast below.
      // Returning true from onErrorData means "I've handled it".
      if (onErrorData && onErrorData(data)) {
        return null;
      }

      const errorMessage = resolveErrorMessage(data, t, "CREATE_FAILED");
      console.error("Create data failed:", errorMessage);
      if (showAlert) {
        showAlert(errorMessage, "error");
      }
      return null;
    }

    return { data, headers: response.headers };
  } catch (error) {
    console.error("Error creating data:", error);
    if (showAlert && t) {
      showAlert(t("NETWORK_ERROR"), "error");
    }
    return null;
  }
};

export const createPublicData = async (
  API_URL,
  dataObject,
  showAlert,
  t,
  forgot,
) => {
  const FULL_API_URL = `${REACT_API_ROUTE}${API_URL}`;
  console.log("Public POST", FULL_API_URL, dataObject);

  try {
    const response = await fetch(FULL_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(dataObject),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error("Public post failed:", response.statusText);
      const errorMessage = forgot
        ? t(
            "IF_THE_ACCOUNT_EXISTS_PASSWORD_RESET_INSTRUCTIONS_HAVE_BEEN_SENT_TO_YOUR_EMAIL",
          )
        : resolveErrorMessage(data, t, "CREATE_FAILED");
      throw new Error(errorMessage);
    }

    return { data, headers: response.headers };
  } catch (error) {
    throw error;
  }
};

export const updateData = async (
  API_URL,
  updatedData,
  showAlert,
  navigate,
  t,
) => {
  const FULL_API_URL = `${REACT_API_ROUTE}${API_URL}`;
  console.log("Update", FULL_API_URL, updatedData);

  try {
    const response = await fetch(FULL_API_URL, {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify(updatedData),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error("Update data failed:", data?.errors || response.statusText);
      if (data?.status === "UNAUTHORIZED" || response.status === 401) {
        handleSessionExpired(showAlert, navigate, t);
        return null;
      }

      const errorMessage = resolveErrorMessage(data, t, "UPDATE_FAILED");
      if (showAlert) {
        showAlert(errorMessage, "error");
      }
      return null;
    }

    return { data, headers: response.headers };
  } catch (error) {
    console.error("Error updating data:", error);
    if (showAlert && t) {
      showAlert(t("NETWORK_ERROR"), "error");
    }
    return null;
  }
};

export const updatePublicData = async (
  API_URL,
  dataObject,
  showAlert,
  navigate,
  t,
) => {
  const FULL_API_URL = `${REACT_API_ROUTE}${API_URL}`;
  console.log("Public PUT", FULL_API_URL, dataObject);

  try {
    const response = await fetch(FULL_API_URL, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(dataObject),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error("Public put failed:", data?.errors || response.statusText);
      const errorMessage = resolveErrorMessage(data, t, "UPDATE_FAILED");
      if (showAlert) {
        showAlert(errorMessage, "error");
      }
      return null;
    }

    return { data, headers: response.headers };
  } catch (error) {
    console.error("Error in updatePublicData:", error);
    if (showAlert && t) {
      showAlert(t("NETWORK_ERROR"), "error");
    }
    return null;
  }
};

export const deleteData = async (
  API_URL,
  data = null,
  showAlert,
  navigate,
  t,
) => {
  const FULL_API_URL = `${REACT_API_ROUTE}${API_URL}`;
  console.log("Delete", FULL_API_URL);

  try {
    const options = {
      method: "DELETE",
      headers: getHeaders(),
    };

    if (Array.isArray(data) || typeof data === "object") {
      options.body = JSON.stringify(data);
    }

    const response = await fetch(FULL_API_URL, options);
    const responseData = await response.json();

    if (!response.ok) {
      console.error(
        "Delete data failed:",
        responseData?.errors || response.statusText,
      );
      if (responseData?.status === "UNAUTHORIZED" || response.status === 401) {
        showAlert(t("SESSION_EXPIRED"), "error");
        navigate("/login");
        return null;
      }

      const errorMessage = resolveErrorMessage(responseData, t, "DELETE_FAILED");
      showAlert(errorMessage, "error");
      return null;
    }

    return { data: responseData, headers: response.headers };
  } catch (error) {
    console.error("Error deleting data:", error);
    showAlert(t("NETWORK_ERROR"), "error");
    return null;
  }
};

export const verifyEmailAndPassword = async (API_URL, username, password) => {
  const FULL_API_URL = `${REACT_API_ROUTE}${API_URL}`;
  console.log(
    "verification email and password: " +
      username +
      " " +
      password +
      " " +
      FULL_API_URL,
  );
  try {
    const response = await fetch(FULL_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password }),
    });

    console.log(response);
    // console.log(response.headers.get("Set-Cookie"));
    // document.cookie = response.headers;
    const data = await response.json();
    if (response.ok) {
      console.log("data", data);
      console.log("cookie", document.cookie);
      return data;
    } else {
      console.error("Verification failed:", response.statusText, data);
      return { error: data };
    }
  } catch (error) {
    console.error("Error during verification:", error);
    return { error: null };
  }
};

export const loginWithCode = async (API_URL, username, otp) => {
  console.log(API_URL, username, otp);
  const FULL_API_URL = `${REACT_API_ROUTE}${API_URL}`;
  console.log(FULL_API_URL);
  try {
    const response = await fetch(FULL_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, otp }),
    });
    console.log(response);
    const data = await response.json();
    console.log("data", data);

    if (response.ok) {
      return data;
      // console.log("data", data);
      // const token = data.token;

      // localStorage.setItem("authToken", token);
      // window.location.href = "/";
    } else {
      return { error: data };
    }
  } catch (error) {
    console.error("Error during login:", error);
    return { error: null };
  }
};

export const refreshToken = async () => {
  const token = localStorage.getItem("authToken");
  if (!token || !apiRoutes.refreshToken) return null;

  const FULL_API_URL = `${REACT_API_ROUTE}${apiRoutes.refreshToken}`;
  try {
    const response = await fetch(FULL_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) return null;

    const data = await response.json();
    const newToken = data?.token;
    if (!newToken) return null;

    localStorage.setItem("authToken", newToken);
    window.dispatchEvent(new Event("auth-token-changed"));
    return newToken;
  } catch (error) {
    console.error("Error refreshing token:", error);
    return null;
  }
};

export const logout = async (API_URL, token) => {
  const FULL_API_URL = `${REACT_API_ROUTE}${API_URL}`;
  try {
    const response = await fetch(FULL_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      console.warn("Logout API call failed, but proceeding with local logout");
    }
  } catch (error) {
    console.error("Error calling logout endpoint:", error);
  }
};

export const handleLogout = async () => {
  const token = localStorage.getItem("authToken");

  if (token && apiRoutes.logout) {
    try {
      await logout(apiRoutes.logout, token);
    } catch (error) {
      console.error("Error during logout API call:", error);
    }
  }

  localStorage.removeItem("authToken");
  localStorage.removeItem("responseLogin");
  window.location.href = "/login";
};

export const verifyToken = async (API_URL, token) => {
  const FULL_API_URL = `${REACT_API_ROUTE}${API_URL}`;
  try {
    const response = await fetch(`${FULL_API_URL}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      throw new Error("Failed to verify token");
    }

    const data = await response.json();
    return data.valid;
  } catch (error) {
    console.error("Error verifying token:", error);
    return false;
  }
};

export const uploadFile = async (
  API_URL,
  file,
  showAlert,
  navigate,
  t,
  info,
) => {
  const FULL_API_URL = `${REACT_API_ROUTE}${API_URL}`;
  console.log("Uploading file to", FULL_API_URL, file);
  console.log("info", info);

  const formData = new FormData();
  formData.append("file", file);

  if (info) {
    formData.append("grupaId", info);
  }

  try {
    const response = await fetch(FULL_API_URL, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: formData,
    });

    const data = await response.json();
    console.log("Upload response data:", data);

    if (!response.ok) {
      console.error("Upload failed:", data?.errors || response.statusText);
      if (data?.status === "UNAUTHORIZED" || response.status === 401) {
        handleSessionExpired(showAlert, navigate, t);
        return { error: true, code: "UNAUTHORIZED" };
      }

      const errorMessage = resolveErrorMessage(data, t, "UPLOAD_FAILED");
      showAlert?.(errorMessage, "error");
      console.log("cao");
      return { data };
    }

    return data;
  } catch (error) {
    console.error("Upload error:", error);
    showAlert?.(t("NETWORK_ERROR"), "error");
    return { error: true, code: "NETWORK_ERROR", message: error.message };
  }
};

export const downloadFile = async (
  API_URL,
  filename,
  showAlert,
  navigate,
  t,
) => {
  const FULL_API_URL = `${REACT_API_ROUTE}${API_URL}`;
  console.log("Downloading file from", FULL_API_URL);

  try {
    const response = await fetch(FULL_API_URL, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
    });

    if (!response.ok) {
      console.error("Download failed:", response.statusText);
      if (response.status === 401) {
        handleSessionExpired(showAlert, navigate, t);
        return null;
      }

      showAlert?.(t("DOWNLOAD_FAILED"), "error");
      throw new Error(`Download failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error("Download error:", error);
    showAlert?.(t("NETWORK_ERROR"), "error");
    return null;
  }
};

export const sendEmails = async (API_URL, recipients, text) => {
  const FULL_API_URL = `${REACT_API_ROUTE}${API_URL}`;
  console.log("Sending emails to:", recipients, FULL_API_URL);

  try {
    const response = await fetch(FULL_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ recipients, text }),
    });

    if (!response.ok) {
      console.error("Email sending failed:", response.statusText);
      throw new Error("Error sending emails");
    }

    console.log("Emails sent successfully");
    return { success: true };
  } catch (error) {
    console.error("Email sending error:", error);
    return { error: true, message: error.message };
  }
};

export const uploadAndValidateImage = async (API_URL, imageFile, extraHeaders = {}) => {
  const FULL_API_URL = `${REACT_API_ROUTE}${API_URL}`;
  console.log("Uploading and validating image to", FULL_API_URL);
  console.log(imageFile);

  const formData = new FormData();
  formData.append("image", imageFile);

  const token = localStorage.getItem("authToken");
  const headers = {
    ...(token && { Authorization: `Bearer ${token}` }),
    ...extraHeaders,
  };

  try {
    const response = await fetch(FULL_API_URL, {
      method: "POST",
      headers,
      body: formData,
    });

    const data = await response.json().catch(() => null);

    if (response.ok) {
      return data;
    }

    console.error("Image upload failed:", response.status, data);
    return {
      error: true,
      status: response.status,
      // gs.fr (FastAPI) rejections carry the reason in `detail`, not `errors`
      errors: data?.errors || (data?.detail ? [data.detail] : undefined),
      errorKey: data?.errorKey,
      reasons: data?.reasons,
    };
  } catch (error) {
    console.error("Error uploading or validating image:", error);
    return { error: true, errors: [error.message] };
  }
};

export const exportToCSV = (data, columns, filename) => {
  if (!data || data.length === 0) {
    console.warn("No data to export");
    return;
  }

  const headers = columns
    .filter((col) => col.name !== "actions")
    .map((col) => col.title);

  const rows = data.map((row) => {
    return columns
      .filter((col) => col.name !== "actions")
      .map((col) => {
        const value = row[col.name] || "";
        const stringValue = String(value);
        if (
          stringValue.includes(",") ||
          stringValue.includes("\n") ||
          stringValue.includes('"')
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
  });

  const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");

  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "export.csv";
  link.click();

  window.URL.revokeObjectURL(url);
};
